import { round } from './round';
import { getOrderEffectiveAmount } from './orderEffectiveAmount';

// "Deduct invoices" quick action of the order-based invoice: the selected
// previous invoices become negative custom positions, collected in one group
// at the end of the invoice.
//
// It exists for imported orders whose billed figures are 0 although invoices
// exist — there the order-effective share of a previous invoice is 0 as well,
// so the deduction is based on each invoice's full net amount. When the
// invoice's positions are loaded, one line per tax rate is created so
// mixed-rate invoices deduct the right tax; otherwise a single line at the
// invoice's order tax rate is used.
//
// The lines are plain custom positions (no isGenerated / orderPositionId) and
// are not settled from the order, so they don't alter the order's billed
// figures. The "Order Positions" step keeps them when it (re)imports order
// lines and the user can still edit or delete them in "Custom Positions".

export function deductionPositionName(inv) {
  return `${inv?.nr} ${inv?.title}`;
}

// True when the invoice already has a deduction line on this invoice — guards
// against adding the same invoice twice on a repeated click. Generated
// final-invoice lines carry the same name but are not quick-action lines.
export function hasDeductionLine(positions, inv) {
  const name = deductionPositionName(inv);
  return (positions || []).some(
    (pos) =>
      pos.name === name &&
      Number(pos.price) < 0 &&
      !pos.isGenerated &&
      pos.type !== 'final',
  );
}

export function buildDeductionPositions(inv, fallbackTaxRate = 19) {
  const taxRate = inv?.orderTaxRate ?? fallbackTaxRate;
  const nettoByTaxRate = new Map();

  if (Array.isArray(inv?.positions) && inv.positions.length > 0) {
    inv.positions.forEach((pos) => {
      const netto = round(pos.price * pos.amount) || 0;
      if (!netto) return;
      const rate = pos.taxRate ?? taxRate;
      nettoByTaxRate.set(rate, round((nettoByTaxRate.get(rate) || 0) + netto));
    });
  }
  if (nettoByTaxRate.size === 0) {
    nettoByTaxRate.set(taxRate, getOrderEffectiveAmount(inv, true).netto);
  }

  return Array.from(nettoByTaxRate.entries())
    .filter(([, netto]) => netto !== 0)
    .map(([rate, netto]) => ({
      name: deductionPositionName(inv),
      description: '',
      amount: 1,
      price: -netto,
      taxRate: rate,
      type: 'default',
      status: 0,
      unit: '',
      depth: 1,
      isPostPosition: false,
      positionableType: 'custom',
      isOrderPosition: false,
      isGroup: false,
    }));
}

function isDeductionGroup(pos, groupName) {
  return (
    Boolean(pos.isGroup) &&
    !(pos.depth > 0) &&
    pos.name === groupName &&
    !pos.isGenerated &&
    pos.type !== 'final'
  );
}

// Appends the deduction lines to the invoice's positions inside a group named
// groupName (with a subtotal). An existing deduction group is reused so
// repeated clicks add to it instead of opening a second group.
//
// The backend requires a position number on every line. The positions table
// renumbers on mount, but a draft can be saved without ever opening it — so
// the group continues the numbering of the existing top-level lines and its
// children are numbered below it.
export function appendDeductionGroup(existing, deductions, groupName) {
  const positions = [...(existing || [])];
  if (!deductions || deductions.length === 0) return positions;

  let groupIndex = positions.findIndex((pos) =>
    isDeductionGroup(pos, groupName),
  );
  if (groupIndex === -1) {
    const topLevelCount = positions.filter(
      (pos) => !(pos.depth > 0) && !pos.isPostPosDivider,
    ).length;
    positions.push({
      position: String(topLevelCount + 1),
      name: groupName,
      description: '',
      amount: 0,
      price: 0,
      taxRate: 0,
      type: 'default',
      status: 0,
      unit: '',
      depth: 0,
      isPostPosition: false,
      positionableType: 'custom',
      isOrderPosition: false,
      isGroup: true,
      showSubtotal: true,
    });
    groupIndex = positions.length - 1;
  }

  const groupNumber = positions[groupIndex].position;
  let end = groupIndex + 1;
  while (
    end < positions.length &&
    positions[end].depth > 0 &&
    !positions[end].isPostPosDivider
  ) {
    end++;
  }
  const childCount = end - groupIndex - 1;

  const children = deductions.map((pos, index) => ({
    ...pos,
    depth: 1,
    position: `${groupNumber}.${childCount + index + 1}`,
  }));
  positions.splice(end, 0, ...children);

  return positions;
}
